import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { UserRole } from "@comp-sys/shared";
import { useAuthStore } from "@/stores/auth-store";
import { authApi } from "@/lib/api/auth";
import { canAccessRoute, roleHome } from "@/hooks/use-navigation";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useTranslations } from "@/i18n/useTranslations";

interface RoleSwitcherMenuItemsProps {
  /** 切换成功后回调(sidebar 用于关闭菜单)。失败时不调用。 */
  onSwitchSuccess?: () => void;
}

/**
 * 头像下拉菜单内的角色切换菜单项(多角色用户)。
 *
 * 每个角色一行;当前 activeRole 打勾 + 禁用;点击其他角色调用 `/auth/switch-role`
 * 重签 token、更新 store、做路由重校验(不可达则跳该角色首页)。成功后调用
 * onSwitchSuccess(菜单关闭);失败弹 toast 且菜单保持打开。
 *
 * 单角色用户返回 null(不渲染角色区)。
 *
 * 实现说明:本项目 dropdown-menu 基于 base-ui(`@base-ui/react/menu`),其 Item
 * 没有 radix 的 `onSelect` + `event.preventDefault()` 关闭控制,而是通过
 * `closeOnClick?: boolean`(默认 true)控制点击后是否关闭。这里设
 * `closeOnClick={false}`,在 `handleSwitch` 成功后再调 `onSwitchSuccess`
 * (sidebar 用受控 `open`/`onOpenChange` 关闭菜单);失败时菜单保持打开。
 */
export function RoleSwitcherMenuItems({ onSwitchSuccess }: RoleSwitcherMenuItemsProps) {
  const user = useAuthStore((s) => s.user);
  const switchRole = useAuthStore((s) => s.switchRole);
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState<UserRole | null>(null);
  const tUser = useTranslations("user");

  if (!user || !user.roles || user.roles.length <= 1) return null;

  const handleSwitch = async (role: UserRole) => {
    if (role === user.activeRole || pending) return;
    setPending(role);
    try {
      const res = await authApi.switchRole(role);
      switchRole(role, res.accessToken, res.refreshToken);
      const currentPath = location.pathname || window.location.pathname;
      if (!canAccessRoute(role, currentPath)) {
        navigate(roleHome(role), { replace: true });
      }
      onSwitchSuccess?.();
    } catch {
      toast.error(tUser("switchRoleFailed"));
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      {user.roles.map((role) => {
        const isCurrent = role === user.activeRole;
        const isPending = pending === role;
        return (
          <DropdownMenuItem
            key={role}
            data-testid={`switch-to-${role}`}
            disabled={isCurrent || !!pending}
            closeOnClick={false}
            onClick={() => handleSwitch(role)}
          >
            <span className="inline-flex w-4 items-center justify-center">
              {isCurrent ? (
                <Check className="h-4 w-4" />
              ) : isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
            </span>
            {tUser(`role.${role}`)}
          </DropdownMenuItem>
        );
      })}
      <DropdownMenuSeparator />
    </>
  );
}

export default RoleSwitcherMenuItems;
